import React, { useMemo, useState } from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import Message from "shared/Message";
import ButtonRounded from "shared/Button";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "contexts/auth";
import { environments } from "constants/environments";
import { IApiMetaData } from "interfaces/apiMetaData";
import { callApi } from "apiServices/callApi";
import { IUpdateProgramInputs, updateProgramSchema } from "schema/program";

interface DropdownItem {
  name: string;
  value: boolean | string;
}

interface IProps {
  rowData: any;
  onSave: () => void;
}

const UpdateProgram = ({ rowData, onSave }: IProps) => {
  const [isResponse, setIsResponse] = useState(false);
  const [result, setResult] = useState<any>({});
  const { sharedData } = useAuth();
  const programAPI =
    environments.domain + sharedData?.apisList?.WebSite?.Program?.General;

  const {
    handleSubmit,
    register,
    control,
    formState: { errors, isSubmitting },
  } = useForm<IUpdateProgramInputs>({
    resolver: zodResolver(updateProgramSchema),
    defaultValues: { ...rowData },
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<IUpdateProgramInputs> = async (
    data: IUpdateProgramInputs
  ) => {
    setIsResponse(false);

    const response: IApiMetaData = await callApi(
      "PUT",
      programAPI,
      data,
      {},
      { id: rowData._id }
    );
    setResult(response);

    if (response.Status === 200) {
      onSave();
      setIsResponse(true);
      // Optionally reset the form or add some success behavior
    }
  };

  const statusOptions: DropdownItem[] = useMemo(
    () => [
      { name: "Active", value: true },
      { name: "Not Active", value: false },
    ],
    []
  );

  return (
    <div className="grid justify-content-center">
      <div className="col-12">
        <div className="px-3">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="p-fluid formgrid p-4 grid border-round-md">
              <div className="field col-12 md:col-12 lg:col-6 xl:col-6">
                <label htmlFor="name">Name</label>
                <InputText
                  id="name"
                  type="text"
                  {...register("name", {
                    setValueAs: (v) => v?.trim(),
                  })}
                />
                {errors?.name && (
                  <small className="text-error text-red-600">
                    {errors?.name?.message}
                  </small>
                )}
              </div>

              <div className="field col-12 md:col-12 lg:col-6 xl:col-6">
                <label htmlFor="status">Status</label>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <Dropdown
                        id="status"
                        value={value}
                        options={statusOptions}
                        onChange={(e) => onChange(e.value)}
                        optionLabel="name"
                        optionValue="value"
                        placeholder="Select Status"
                      />
                      {errors?.isActive && (
                        <small className="text-error text-red-600">
                          {errors?.isActive?.message}
                        </small>
                      )}
                    </>
                  )}
                />
              </div>

              <div className="text-right mt-4 ml-auto">
                <ButtonRounded
                  disabled={isSubmitting}
                  label="Save"
                  raised
                  severity="success"
                />
              </div>
            </div>
          </form>
        </div>
      </div>
      {isResponse && (
        <div className="col-8">
          <Message
            message={
              result?.Status === 200
                ? "Program updated successfully!"
                : result?.response?.data?.Error
            }
            severity={result?.Status === 200 ? "success" : "error"}
          />
        </div>
      )}
    </div>
  );
};

export default UpdateProgram;
